import type { GSIconName } from 'src/components/shared/GSIcon'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { ADMIN_ROUTES } from 'src/constants/routes'

// The 12 customer-setup tab slugs. Keeping this a union (rather than `string`) means a typo in
// ADMIN_DASHBOARD_CUSTOMER_TABS or in AppRoutes' CUSTOMER_TAB_ELEMENTS lookup fails typechecking
// instead of silently falling through to AppPlaceholderPage.
export type CustomerTabSlug =
  | 'products'
  | 'markets'
  | 'docusign'
  | 'onboarding-templates'
  | 'settings'
  | 'payments'
  | 'users'
  | 'insurance'
  | 'business'
  | 'invoice'
  | 'oauth'
  | 'webhooks'
  | 'ai-review'

interface AdminDashboardTab {
  slug: CustomerTabSlug
  label: string
  // The Linear issue that will build this tab. Rendered by AppPlaceholderPage until then.
  issueId: string
  iconName: GSIconName
  // Rail entry and route are hidden from someone holding none of these (ENG-1207 part 2). Each is
  // the view permission of the GET the page loads — see the 2026-10-13 permission gating spec.
  requiredAnyPermission: InternalPermission[]
}

// The three global (non-customer-scoped) page slugs. Keeping this a union rather than `string`
// means a typo in ADMIN_DASHBOARD_GLOBAL_SECTIONS or in AppRoutes' GLOBAL_ROUTE_ELEMENTS lookup
// fails typechecking instead of silently falling through to AppPlaceholderPage.
export type AdminGlobalSlug = 'customers' | 'insurance-products' | 'lms-courses'

interface AdminDashboardNavItem {
  slug: AdminGlobalSlug
  to: string
  label: string
  // The Linear issue that will build this tab. Rendered by AppPlaceholderPage until then.
  issueId: string
  iconName: GSIconName
  // Rail entry and route are hidden from someone holding none of these (ENG-1207 part 2). Each is
  // the view permission of the GET the page loads — see the 2026-10-13 permission gating spec.
  requiredAnyPermission: InternalPermission[]
}

interface AdminDashboardNavSection {
  label: string
  items: AdminDashboardNavItem[]
}

// The 13 customer-setup tabs. The approved prototype
// (apps/admin-prototype/public/apps/admin-dashboard.html) has only 11 — it omits Products
// entirely. That divergence is intentional: product entitlement gates the tabs after it, so
// it leads the list. See the design doc's §8.1 before "correcting" this back to 11.
export const ADMIN_DASHBOARD_CUSTOMER_TABS: AdminDashboardTab[] = [
  {
    slug: 'products',
    label: 'Products',
    issueId: 'ENG-691',
    iconName: 'gs_package',
    requiredAnyPermission: [InternalPermission.PartnerProductsView],
  },
  {
    slug: 'markets',
    label: 'Markets',
    issueId: 'ENG-475',
    iconName: 'gs_map_pin',
    requiredAnyPermission: [InternalPermission.MarketsView],
  },
  {
    slug: 'docusign',
    label: 'Docusign',
    issueId: 'ENG-476',
    iconName: 'gs_signature',
    requiredAnyPermission: [InternalPermission.DocusignTemplatesView],
  },
  {
    slug: 'onboarding-templates',
    label: 'Onboarding Templates',
    issueId: 'ENG-477',
    iconName: 'gs_tasks',
    requiredAnyPermission: [InternalPermission.CollectDataTemplatesView],
  },
  {
    slug: 'ai-review',
    label: 'AI Review',
    issueId: 'ENG-644',
    iconName: 'gs_shield_check',
    requiredAnyPermission: [InternalPermission.AiDocumentReviewConfigsView],
  },
  {
    slug: 'settings',
    label: 'Settings',
    issueId: 'ENG-478',
    iconName: 'gs_settings_gear_1',
    requiredAnyPermission: [InternalPermission.PartnerSettingsView],
  },
  {
    slug: 'payments',
    label: 'Payments',
    issueId: 'ENG-479',
    iconName: 'gs_wallet_3',
    requiredAnyPermission: [InternalPermission.PayoutSettingsView],
  },
  {
    slug: 'users',
    label: 'Users',
    issueId: 'ENG-480',
    iconName: 'gs_user_group',
    requiredAnyPermission: [InternalPermission.CustomerUsersView],
  },
  {
    slug: 'insurance',
    label: 'Insurance',
    issueId: 'ENG-481',
    iconName: 'gs_shield',
    requiredAnyPermission: [InternalPermission.InsurancePoliciesView],
  },
  {
    slug: 'business',
    label: 'Business',
    issueId: 'ENG-482',
    iconName: 'gs_buildings',
    requiredAnyPermission: [InternalPermission.OatfiView],
  },
  {
    slug: 'invoice',
    label: 'Invoice',
    issueId: 'ENG-483',
    iconName: 'gs_receipt_bill',
    requiredAnyPermission: [InternalPermission.InvoicesView],
  },
  {
    slug: 'oauth',
    label: 'OAuth Client',
    issueId: 'ENG-484',
    iconName: 'gs_key_1',
    requiredAnyPermission: [InternalPermission.OauthClientsView],
  },
  {
    slug: 'webhooks',
    label: 'Webhooks',
    issueId: 'ENG-485',
    iconName: 'gs_webhooks',
    // Subscriptions and deliveries are separate GETs; either one makes the tab useful.
    requiredAnyPermission: [
      InternalPermission.WebhookSubscriptionsView,
      InternalPermission.WebhookDeliveriesView,
    ],
  },
]

export const ADMIN_DASHBOARD_GLOBAL_SECTIONS: AdminDashboardNavSection[] = [
  {
    label: 'Customers',
    items: [
      {
        slug: 'customers',
        to: ADMIN_ROUTES.ADMIN_DASHBOARD_CUSTOMERS_URL,
        label: 'Customer list',
        issueId: 'ENG-473',
        iconName: 'gs_people',
        requiredAnyPermission: [InternalPermission.PartnersView],
      },
    ],
  },
  {
    label: 'Global catalog',
    items: [
      {
        slug: 'insurance-products',
        to: ADMIN_ROUTES.ADMIN_DASHBOARD_INSURANCE_PRODUCTS_URL,
        label: 'Insurance Products',
        issueId: 'ENG-474',
        iconName: 'gs_shield_check',
        requiredAnyPermission: [InternalPermission.InsuranceProductsView],
      },
      {
        slug: 'lms-courses',
        to: ADMIN_ROUTES.ADMIN_DASHBOARD_LMS_COURSES_URL,
        label: 'LMS Courses',
        issueId: 'ENG-474',
        iconName: 'gs_book',
        requiredAnyPermission: [InternalPermission.LmsTrainingsView],
      },
    ],
  },
]

export const ADMIN_DASHBOARD_GLOBAL_ITEMS: AdminDashboardNavItem[] =
  ADMIN_DASHBOARD_GLOBAL_SECTIONS.flatMap((section) => {
    return section.items
  })

export const buildCustomerTabPath = (customerId: string, slug: string): string => {
  return `${ADMIN_ROUTES.ADMIN_DASHBOARD_CUSTOMERS_URL}/${customerId}/${slug}`
}

export const buildCustomerOverviewPath = (customerId: string): string => {
  return `${ADMIN_ROUTES.ADMIN_DASHBOARD_CUSTOMERS_URL}/${customerId}`
}

export const buildCustomerInsurancePolicyPath = (
  customerId: string,
  insurancePolicyPermanentId: string,
): string => {
  return `${ADMIN_ROUTES.ADMIN_DASHBOARD_CUSTOMERS_URL}/${customerId}/insurance/${insurancePolicyPermanentId}`
}

export const buildCustomerInvoiceDetailsPath = (
  customerId: string,
  composedInvoiceId: string,
): string => {
  return `${ADMIN_ROUTES.ADMIN_DASHBOARD_CUSTOMERS_URL}/${customerId}/invoice/${composedInvoiceId}`
}

export const ADMIN_DASHBOARD_CUSTOMER_OVERVIEW: { label: string; iconName: GSIconName } = {
  label: 'Overview',
  iconName: 'gs_home',
}
